import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glow, glowColor, bake, Gait } from './units-style.js';

// The rifle arm is bent forward, carrying the carbine across the chest so it
// stays inside the commander's collision radius
const AIM_POSE = -0.45;
const CARRY_YAW = -1.2;

// The hero: a power-armoured commander with team plating, a glowing visor,
// a thruster pack and a pulse rifle
export class Player {
  constructor(scene, unit, color, isLocalPlayer = false, displayName = null) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isLocalPlayer = isLocalPlayer;
    this.displayName = displayName || `Player ${unit.ownerId + 1}`;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.respawnOverlay = null;
    this.nametagSprite = null;
    this.healthBar = null;
    this.maxHealth = 10; // PlayerUnitHealth from server
    this.isRespawning = null;
    this.geometries = [];
    this.materials = [];
    this.create();
  }

  // Armour and glow parts merged into one mesh each, using this commander's materials
  part(parts, glowParts = []) {
    const group = new THREE.Group();
    const geometry = bake(parts);
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    if (glowParts.length) {
      const glowGeometry = bake(glowParts);
      this.geometries.push(glowGeometry);
      group.add(new THREE.Mesh(glowGeometry, this.glowMaterial));
    }
    return group;
  }

  // A pivoting limb attached to the body
  limb(parts, glowParts, x, y, z) {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    pivot.add(this.part(parts, glowParts));
    this.body.add(pivot);
    return pivot;
  }

  create() {
    const bodyGroup = new THREE.Group();

    // Per-instance materials so respawn dimming only affects this commander
    this.material = armor({ unique: true });
    this.glowMaterial = glow({ unique: true });
    for (const material of [this.material, this.glowMaterial]) {
      material.userData.baseOpacity = 1;
      this.materials.push(material);
    }

    const team = new THREE.Color(this.color);
    const teamDark = team.clone().multiplyScalar(0.6);
    const trim = glowColor(this.color);

    // Everything that bobs while walking hangs off the body
    this.body = new THREE.Group();
    bodyGroup.add(this.body);

    // Torso: gunmetal undersuit, team chest plate, white trim, glowing core.
    // Helmet with a wide visor, big team shoulder plates and a thruster pack.
    this.body.add(this.part([
      colored(new THREE.CylinderGeometry(0.5, 0.6, 0.42, 8), SCIFI.gunmetal, { y: 0.98 }),
      colored(new THREE.BoxGeometry(1.0, 0.24, 0.66), SCIFI.alloyDark, { y: 1.22 }),
      colored(new THREE.BoxGeometry(0.92, 1.0, 0.6), SCIFI.gunmetal, { y: 1.8 }),
      colored(new THREE.BoxGeometry(0.86, 0.72, 0.18), team, { y: 1.95, z: 0.3, rx: -0.12 }),
      colored(new THREE.BoxGeometry(0.5, 0.4, 0.16), SCIFI.white, { y: 1.5, z: 0.3 }),
      colored(new THREE.BoxGeometry(0.88, 0.07, 0.2), SCIFI.white, { y: 2.3, z: 0.27, rx: -0.12 }),
      colored(new THREE.CylinderGeometry(0.3, 0.38, 0.2, 8), SCIFI.alloyDark, { y: 2.4 }),
      // Helmet: white dome, gunmetal jaw, team crest
      colored(new THREE.SphereGeometry(0.5, 8, 6), SCIFI.white, { y: 2.88, scale: [1, 1.0, 1.08] }),
      colored(new THREE.BoxGeometry(0.72, 0.3, 0.5), SCIFI.gunmetal, { y: 2.62, z: 0.18 }),
      colored(new THREE.BoxGeometry(0.8, 0.26, 0.12), SCIFI.black, { y: 2.9, z: 0.45 }),
      colored(new THREE.BoxGeometry(0.16, 0.2, 0.8), team, { y: 3.35, z: -0.02 }),
      colored(new THREE.CylinderGeometry(0.02, 0.02, 0.5, 4), SCIFI.alloy, { x: -0.42, y: 3.2, z: -0.15 }),
      // Shoulder plates in team colour with white rims
      colored(new THREE.BoxGeometry(0.46, 0.32, 0.5), team, { x: -0.56, y: 2.27, rz: 0.3 }),
      colored(new THREE.BoxGeometry(0.46, 0.32, 0.5), team, { x: 0.56, y: 2.27, rz: -0.3 }),
      colored(new THREE.BoxGeometry(0.46, 0.08, 0.52), SCIFI.white, { x: -0.6, y: 2.1, rz: 0.3 }),
      colored(new THREE.BoxGeometry(0.46, 0.08, 0.52), SCIFI.white, { x: 0.6, y: 2.1, rz: -0.3 }),
      // Thruster pack with twin nozzles
      colored(new THREE.BoxGeometry(0.76, 0.92, 0.36), SCIFI.alloyDark, { y: 1.85, z: -0.46 }),
      colored(new THREE.BoxGeometry(0.56, 0.5, 0.1), teamDark, { y: 1.95, z: -0.66 }),
      colored(new THREE.CylinderGeometry(0.15, 0.2, 0.32, 6), SCIFI.gunmetal, { x: -0.24, y: 1.28, z: -0.55 }),
      colored(new THREE.CylinderGeometry(0.15, 0.2, 0.32, 6), SCIFI.gunmetal, { x: 0.24, y: 1.28, z: -0.55 })
    ], [
      colored(new THREE.BoxGeometry(0.66, 0.12, 0.06), trim, { y: 2.92, z: 0.51 }),
      colored(new THREE.BoxGeometry(0.2, 0.2, 0.06), trim, { y: 1.5, z: 0.43 }),
      colored(new THREE.BoxGeometry(0.05, 0.05, 0.05), trim, { x: -0.42, y: 3.47, z: -0.15 }),
      colored(new THREE.BoxGeometry(0.1, 0.5, 0.04), trim, { x: -0.25, y: 1.95, z: -0.72 }),
      colored(new THREE.BoxGeometry(0.1, 0.5, 0.04), trim, { x: 0.25, y: 1.95, z: -0.72 })
    ]));

    // Thruster flames under the pack, flickering
    const flameGeometry = bake([
      colored(new THREE.ConeGeometry(0.13, 0.4, 6), trim, { x: -0.24, y: -0.2, rx: Math.PI }),
      colored(new THREE.ConeGeometry(0.13, 0.4, 6), trim, { x: 0.24, y: -0.2, rx: Math.PI })
    ]);
    this.geometries.push(flameGeometry);
    this.flame = new THREE.Mesh(flameGeometry, this.glowMaterial);
    this.flame.position.set(0, 1.12, -0.55);
    this.body.add(this.flame);

    // Legs: armoured thighs, white knee guards, team shin plates, boots
    const leg = () => [
      colored(new THREE.BoxGeometry(0.38, 0.5, 0.4), SCIFI.gunmetal, { y: -0.28 }),
      colored(new THREE.BoxGeometry(0.34, 0.22, 0.12), SCIFI.white, { y: -0.52, z: 0.22 }),
      colored(new THREE.BoxGeometry(0.4, 0.34, 0.12), team, { y: -0.72, z: 0.2 }),
      colored(new THREE.BoxGeometry(0.42, 0.3, 0.52), SCIFI.alloyDark, { y: -0.88, z: 0.05 })
    ];
    this.leftLeg = this.limb(leg(), [], -0.26, 1.05, 0);
    this.rightLeg = this.limb(leg(), [], 0.26, 1.05, 0);

    // Arms: undersuit, white forearm armour, gauntlet
    const arm = () => [
      colored(new THREE.BoxGeometry(0.28, 0.6, 0.3), SCIFI.gunmetal, { y: -0.32 }),
      colored(new THREE.BoxGeometry(0.32, 0.34, 0.34), SCIFI.white, { y: -0.72 }),
      colored(new THREE.BoxGeometry(0.3, 0.2, 0.32), SCIFI.alloyDark, { y: -0.95 })
    ];
    this.leftArm = this.limb(arm(), [], -0.53, 2.12, 0);

    // Compact pulse carbine, built level along +Z around the grip, then baked into
    // the right arm so it lies across the chest with the muzzle to the left
    const rifleParts = [
      colored(new THREE.BoxGeometry(0.18, 0.24, 0.62), SCIFI.alloyDark, { y: 0.1, z: 0.08 }),
      colored(new THREE.BoxGeometry(0.2, 0.08, 0.42), team, { y: 0.26, z: 0.1 }),
      colored(new THREE.BoxGeometry(0.12, 0.14, 0.3), SCIFI.gunmetal, { y: 0.1, z: 0.52 }),
      colored(new THREE.BoxGeometry(0.1, 0.28, 0.14), SCIFI.gunmetal, { y: -0.08, z: 0.0, rx: 0.2 }),
      colored(new THREE.BoxGeometry(0.14, 0.2, 0.2), SCIFI.gunmetal, { y: 0.04, z: -0.3 }),
      colored(new THREE.BoxGeometry(0.08, 0.1, 0.2), SCIFI.black, { y: 0.34, z: 0.05 })
    ];
    const rifleGlow = [
      colored(new THREE.BoxGeometry(0.05, 0.05, 0.36), trim, { x: 0.1, y: 0.08, z: 0.12 }),
      colored(new THREE.BoxGeometry(0.1, 0.1, 0.04), trim, { y: 0.1, z: 0.68 })
    ];
    const pose = geo => {
      geo.rotateY(CARRY_YAW);
      geo.rotateX(-AIM_POSE);
      geo.translate(-0.25, -0.95, 0.06);
      return geo;
    };
    this.rightArm = this.limb([...arm(), ...rifleParts.map(pose)], rifleGlow.map(pose), 0.53, 2.12, 0);
    this.rightArm.rotation.x = AIM_POSE;

    // Indicator for local player
    if (this.isLocalPlayer) {
      const indicatorGeometry = new THREE.RingGeometry(1.5, 2, 32);
      const indicatorMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5
      });
      const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
      indicator.rotation.x = -Math.PI / 2;
      indicator.position.y = 0.1;
      bodyGroup.add(indicator);
      this.indicator = indicator;
    }

    // Create floating nametag
    this.nametagSprite = this.createNametag();
    this.nametagSprite.position.y = 4.5; // Above the head
    bodyGroup.add(this.nametagSprite);

    // Create health bar (between head and nametag)
    this.healthBar = new HealthBar(this.maxHealth, 2.5, 0.3);
    this.healthBar.getGroup().position.y = 3.8;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.gait = new Gait(this.mesh);

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Walk cycle driven by how far the interpolated mesh moved since the last frame
  animate() {
    if (this.gait.step(3, 2.6) <= 0) return;
    const { walk, phase } = this.gait;
    const now = performance.now() / 1000;

    // Strides are kept short so feet and hands stay inside the collision radius
    const swing = Math.sin(phase) * 0.45 * walk;
    this.leftLeg.rotation.x = swing;
    this.rightLeg.rotation.x = -swing;
    this.leftArm.rotation.x = -swing * 0.7;
    this.rightArm.rotation.x = AIM_POSE + Math.sin(phase * 2) * 0.04 * walk;
    this.body.position.y = -Math.abs(Math.sin(phase)) * 0.05 * walk;
    // Thrusters idle low and burn harder on the move
    this.flame.scale.set(1, 0.6 + walk * 0.7 + Math.sin(now * 23) * 0.1 + Math.sin(now * 37) * 0.06, 1);
  }

  // Set respawning state (dims the player)
  setRespawning(isRespawning) {
    if (!this.mesh || this.isRespawning === isRespawning) return;
    this.isRespawning = isRespawning;

    for (const material of this.materials) {
      material.transparent = isRespawning;
      material.opacity = material.userData.baseOpacity * (isRespawning ? 0.3 : 1);
      material.needsUpdate = true;
    }
  }

  // Aim at a target position
  aimAt(targetX, targetZ) {
    if (this.mesh) {
      const dx = targetX - this.mesh.position.x;
      const dz = targetZ - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  // Create a floating nametag sprite
  createNametag() {
    const name = this.displayName;

    // Create canvas for the nametag
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Get background color from player color
    const bgColor = typeof this.color === 'string' ? this.color : `#${this.color.toString(16).padStart(6, '0')}`;

    // Draw rounded rectangle background
    const padding = 10;
    const radius = 12;
    context.fillStyle = bgColor;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, radius);
    context.fill();

    // Add slight border
    context.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    context.lineWidth = 2;
    context.stroke();

    // Draw text
    context.fillStyle = '#ffffff';
    context.font = 'bold 28px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create sprite material
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false, // Always render on top
      depthWrite: false
    });

    // Create sprite
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(4, 1, 1); // Scale to match aspect ratio

    return sprite;
  }

  remove() {
    if (this.nametagSprite) {
      if (this.nametagSprite.material.map) {
        this.nametagSprite.material.map.dispose();
      }
      this.nametagSprite.material.dispose();
    }
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.indicator) {
      this.indicator.geometry.dispose();
      this.indicator.material.dispose();
    }
    this.geometries.forEach(g => g.dispose());
    this.materials.forEach(m => m.dispose());
    this.geometries = [];
    this.materials = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
